import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkle, PencilSimple, Trash, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const MOODS = [
  { key: "calm", label: "calm" },
  { key: "anxious", label: "anxious" },
  { key: "tender", label: "tender" },
  { key: "hopeful", label: "hopeful" },
  { key: "tired", label: "tired" },
  { key: "angry", label: "angry" },
  { key: "grateful", label: "grateful" },
  { key: "lost", label: "lost" },
];

export default function EntryDetailModal({ entryId, initialEntry, onClose, onChanged }) {
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState("");
  const [moods, setMoods] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!entryId) return;
    setEditing(false);
    setConfirmDelete(false);
    // Hydrate immediately from list-provided initialEntry, then refresh in background
    if (initialEntry) {
      setEntry(initialEntry);
      setContent(initialEntry.content);
      setMoods(initialEntry.moods || []);
      setLoading(false);
    } else {
      setLoading(true);
    }
    api
      .get(`/entries/${entryId}`)
      .then((r) => {
        setEntry(r.data);
        setContent(r.data.content);
        setMoods(r.data.moods || []);
      })
      .catch(() => !initialEntry && toast.error("Could not load entry"))
      .finally(() => setLoading(false));
  }, [entryId, initialEntry]);

  const toggleMood = (m) =>
    setMoods((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));

  const save = async () => {
    if (content.trim().length < 3) {
      toast.error("Write a little more.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/entries/${entryId}`, { content, moods });
      setEntry(data);
      setEditing(false);
      onChanged?.();
      toast.success("Updated. The mirror replied again.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/entries/${entryId}`);
      onChanged?.();
      onClose?.();
      toast.success("Entry released.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    } finally {
      setDeleting(false);
    }
  };

  const date = entry?.created_at
    ? new Date(entry.created_at).toLocaleDateString(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : "";

  return (
    <AnimatePresence>
      {entryId && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
          onClick={onClose}
          data-testid="entry-detail-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="bg-canvas border border-line rounded-3xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-canvas/95 backdrop-blur-md border-b border-line px-5 sm:px-8 md:px-10 py-4 sm:py-5 flex items-center justify-between z-10">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-ink-mute">{date}</p>
              </div>
              <div className="flex items-center gap-2">
                {!editing && entry && (
                  <>
                    <button
                      onClick={() => setEditing(true)}
                      data-testid="edit-entry-btn"
                      className="inline-flex items-center gap-1.5 text-sm text-ink-soft hover:text-ink px-3 py-1.5 rounded-full hover:bg-canvas-warm transition-colors"
                    >
                      <PencilSimple size={14} weight="thin" /> Edit
                    </button>
                    <button
                      onClick={() => setConfirmDelete(true)}
                      data-testid="delete-entry-btn"
                      className="inline-flex items-center gap-1.5 text-sm text-ink-soft hover:text-clay-dark px-3 py-1.5 rounded-full hover:bg-clay/10 transition-colors"
                    >
                      <Trash size={14} weight="thin" /> Delete
                    </button>
                  </>
                )}
                <button onClick={onClose} className="text-ink-mute hover:text-ink p-1.5" data-testid="close-entry-modal">
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="px-5 sm:px-8 md:px-10 py-6 sm:py-8">
              {loading ? (
                <div className="space-y-4">
                  <div className="h-6 bg-canvas-warm rounded animate-pulse w-3/4" />
                  <div className="h-6 bg-canvas-warm rounded animate-pulse" />
                  <div className="h-6 bg-canvas-warm rounded animate-pulse w-5/6" />
                </div>
              ) : !entry ? (
                <p className="text-ink-mute">Entry not found.</p>
              ) : editing ? (
                <div>
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    rows={8}
                    data-testid="edit-entry-textarea"
                    className="composer-textarea min-h-[200px]"
                    autoFocus
                  />
                  <div className="mt-6 flex flex-wrap items-center gap-2">
                    <span className="text-xs uppercase tracking-[0.25em] text-ink-mute mr-2">Moods</span>
                    {MOODS.map((m) => {
                      const active = moods.includes(m.key);
                      return (
                        <button
                          key={m.key}
                          onClick={() => toggleMood(m.key)}
                          data-testid={`edit-mood-${m.key}`}
                          className={`text-sm px-3.5 py-1.5 rounded-full transition-all ${
                            active ? "bg-clay/20 text-clay-dark ring-1 ring-clay/40" : "bg-canvas-warm text-ink-soft hover:text-ink"
                          }`}
                        >
                          {m.label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-8 flex items-center gap-4">
                    <button
                      onClick={save}
                      disabled={saving || content.trim().length < 3}
                      data-testid="save-edit-btn"
                      className="inline-flex items-center gap-2 rounded-full bg-clay text-canvas px-6 py-3 text-sm font-medium hover:bg-clay-dark transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {saving ? "Saving & re-reflecting…" : (<><FloppyDisk size={16} weight="bold" /> Save & re-reflect</>)}
                    </button>
                    <button
                      onClick={() => {
                        setEditing(false);
                        setContent(entry.content);
                        setMoods(entry.moods || []);
                      }}
                      className="text-sm text-ink-soft hover:text-ink underline underline-offset-4 decoration-line-strong"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="font-serif-display text-2xl md:text-[28px] text-ink leading-relaxed whitespace-pre-wrap">
                    {entry.content}
                  </p>
                  {entry.moods?.length > 0 && (
                    <div className="mt-6 flex flex-wrap gap-2">
                      {entry.moods.map((m) => (
                        <span key={m} className="text-xs px-3 py-1 rounded-full bg-canvas-warm text-ink-soft border border-line">
                          {m}
                        </span>
                      ))}
                    </div>
                  )}

                  {entry.reflection && (
                    <div className="mt-10 glass-warm rounded-2xl p-7 relative overflow-hidden" data-testid="detail-reflection">
                      <div className="absolute left-0 top-6 bottom-6 w-[2px] bg-clay/40 rounded-full" />
                      <div className="flex items-center gap-2 mb-5">
                        <Sparkle size={14} weight="fill" className="text-clay" />
                        <span className="text-xs uppercase tracking-[0.3em] text-ink-mute">The mirror noticed</span>
                      </div>
                      {entry.reflection.mirror && (
                        <p className="font-serif-display italic text-xl md:text-2xl text-ink leading-snug mb-8 max-w-2xl">
                          {entry.reflection.mirror}
                        </p>
                      )}
                      <div className="grid md:grid-cols-3 gap-6">
                        <div>
                          <p className="text-xs uppercase tracking-[0.25em] text-ink-mute mb-2">Emotions</p>
                          <div className="flex flex-wrap gap-1.5">
                            {entry.reflection.emotions?.map((e) => (
                              <span key={e} className="text-xs px-2.5 py-1 rounded-full bg-clay/15 text-clay-dark">{e}</span>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-[0.25em] text-ink-mute mb-2">Patterns</p>
                          <ul className="space-y-1.5 text-ink-soft text-sm leading-relaxed">
                            {entry.reflection.patterns?.map((p, i) => (
                              <li key={i} className="flex gap-2"><span className="text-clay">·</span>{p}</li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-[0.25em] text-ink-mute mb-2">Questions</p>
                          <ol className="space-y-2 text-ink text-sm leading-relaxed font-serif-display text-base italic">
                            {entry.reflection.gentle_questions?.map((q, i) => (
                              <li key={i} className="flex gap-2">
                                <span className="text-clay font-sans not-italic text-xs">{i + 1}.</span>{q}
                              </li>
                            ))}
                          </ol>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Delete confirmation */}
            <AnimatePresence>
              {confirmDelete && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 bg-canvas/95 backdrop-blur-sm flex items-center justify-center p-8 rounded-3xl"
                  data-testid="delete-confirm"
                >
                  <div className="text-center max-w-sm">
                    <h3 className="font-serif-display text-3xl text-ink mb-3">Release this entry?</h3>
                    <p className="text-ink-soft mb-8 leading-relaxed">
                      Some things are meant to be written and let go. This cannot be undone.
                    </p>
                    <div className="flex items-center justify-center gap-3">
                      <button
                        onClick={() => setConfirmDelete(false)}
                        className="rounded-full border border-line-strong px-6 py-2.5 text-sm hover:bg-canvas-warm transition-colors"
                      >
                        Keep it
                      </button>
                      <button
                        onClick={doDelete}
                        disabled={deleting}
                        data-testid="confirm-delete-btn"
                        className="rounded-full bg-clay text-canvas px-6 py-2.5 text-sm font-medium hover:bg-clay-dark transition-colors disabled:opacity-60"
                      >
                        {deleting ? "Releasing…" : "Release"}
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
